import {
  ExternalLink,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@alice-financial/pretext-ui'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import * as React from 'react'
import { ConnectPaymentMethodStep } from '../../aliceCardProgram/ConnectPaymentMethodStep'
import { UnifiedFundingForm } from '../../aliceCardProgram/setup/UnifiedFundingForm'
import { useCurrentOrgRootPath, useOrg } from '../../organization/useOrg'

export const BankingStepTitle = 'Contributions Account'

export const BankingDescription = () => (
  <Typography variant="body2" color="text.secondary">
    Connect your company bank account for contributions
  </Typography>
)

const AddPayGroups = () => {
  const currentOrgRootPath = useCurrentOrgRootPath()
  const org = useOrg()
  if (!org || org.aliceCardProgram.hasUnifiedBanking || org.isOnboarded) return null
  return (
    <Typography variant="subtitle1">
      Create <strong>additional pay groups</strong> on the{' '}
      <Link to={`${currentOrgRootPath}/locations`}>Locations page</Link>.
    </Typography>
  )
}

// Wrapper component that uses the core Alice Card Program components
export const BankingComponent = () => {
  const org = useOrg()

  if (!org) return null

  return (
    <>
      {org.isOnboarded && (
        <Typography variant="body2" gutterBottom maxWidth="sm">
          Set up your contributions account to fund employee benefits and manage company banking.
        </Typography>
      )}

      <UnifiedFundingForm org={org} />
      <ConnectPaymentMethodStep org={org} />
      <AddPayGroups />
    </>
  )
}

export const BankingInstructions = () => {
  const org = useOrg()
  return (
    <>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Your employees&apos; deductions are used to fund their pretax eligible purchases using Alice Card.
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        You will receive a transfer notice for each transfer and can see your Alice Card funding account
        balance.
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Alice will make regular withdrawals (roughly weekly) to ensure there are adequate funds in the Alice
        Card funding account to cover employees&apos; purchases.
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Check out our{' '}
        <ExternalLink
          href="https://help.thisisalice.com/article/70-for-your-finance-team-how-alice-works-for-employers"
          target="_blank"
        >
          guide for your finance team
        </ExternalLink>{' '}
        for more detail.
      </Typography>

      <Typography variant="subtitle1" fontWeight="bold">
        Requirements
      </Typography>
      <List dense>
        <ListItem disableGutters>
          <ListItemIcon>
            <CheckCircleOutlineIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="Ensure the selected account(s) will not block ACH debits from our accounts" />
        </ListItem>
      </List>

      {!org?.isOnboarded && (
        <>
          <Typography variant="subtitle1" fontWeight="bold">
            Key Info
          </Typography>
          <List dense>
            <ListItem disableGutters>
              <ListItemIcon>
                <LightbulbOutlinedIcon fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText primary="If you have multiple locations, you can upload a file with all of your locations and accounts" />
            </ListItem>
          </List>
        </>
      )}
    </>
  )
}
